import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { CheckCircle2, XCircle, Building2, Loader2 } from 'lucide-react';
import { APP_CONFIG } from '@/config/app';
import { useAuth } from '@/hooks/useAuth';
import { getSessionUser } from '@/integrations/supabase/session';
import { clearPendingOAuthLogin } from '@/lib/loginTracking';
import { AuthDivider, GoogleButton } from '@/components/auth/GoogleButton';
import { useGoogleSignInEnabled } from '@/hooks/useGoogleSignInEnabled';
import {
  clearOAuthInvite,
  clearOAuthParamsFromUrl,
  describeOAuthError,
  markOAuthInvite,
  peekOAuthInvite,
  readOAuthError,
} from '@/lib/oauthReturn';

const signupSchema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

interface CompanyInviteValidation {
  is_valid: boolean;
  invite_id: string | null;
  email: string | null;
  company_name: string | null;
  company_details: Record<string, unknown> | null;
  expires_at: string | null;
  error_message: string | null;
}

export default function CompanyInviteAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Extract token once and store in state. Back from Google the URL carries no
  // token (it was stripped before leaving), so it comes from the round-trip marker.
  const [token] = useState(() => searchParams.get('token') ?? peekOAuthInvite('company'));
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { signInWithGoogle } = useAuth();
  const googleEnabled = useGoogleSignInEnabled();

  const [isValidating, setIsValidating] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteData, setInviteData] = useState<CompanyInviteValidation | null>(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Clean token from URL after extraction (security best practice)
  useEffect(() => {
    if (token && window.location.search.includes('token=')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [token]);

  useEffect(() => {
    void start();
  }, [token]);

  const start = async () => {
    const returningFromGoogle = !!token && peekOAuthInvite('company') === token;
    const oauthError = readOAuthError();
    if (returningFromGoogle || oauthError) {
      clearOAuthInvite();
      clearOAuthParamsFromUrl();
    }

    if (oauthError) {
      clearPendingOAuthLogin();
      toast({
        title: 'Could not continue with Google',
        // No ticket for that address: a different Google account than the invited email.
        description: describeOAuthError(
          oauthError,
          'That Google account does not match this invitation. Choose the Google account for the email address the invitation was sent to.',
        ),
        variant: 'destructive',
      });
    } else if (returningFromGoogle && (await finishGoogleReturn())) {
      return;
    }

    await validateInvite();
  };

  // Back from Google with a session. Normally handle_new_user has created the
  // company while creating the account. If the invite is still pending, Google
  // signed in to an account that already existed, which (as with a password
  // sign-up) can't found a new company. Returns false to show the invite again.
  const finishGoogleReturn = async (): Promise<boolean> => {
    const user = await getSessionUser();
    if (!user) return false;

    const { data, error } = await supabase.rpc('validate_company_invite_token', { p_token: token });
    if (error) return false;
    const invite = (Array.isArray(data) ? data[0] : data) as CompanyInviteValidation | undefined;

    if (!invite?.is_valid) {
      // Company created; the setup wizard takes it from here.
      navigate('/', { replace: true });
      return true;
    }

    await supabase.auth.signOut();
    const sameEmail = (user.email ?? '').toLowerCase() === (invite.email ?? '').toLowerCase();
    toast({
      title: sameEmail ? 'Account Already Exists' : 'Wrong Google account',
      description: sameEmail
        ? 'An account with this email already exists. Please contact support.'
        : `You signed in as ${user.email}, but this invitation is for ${invite.email}. Choose that Google account.`,
      variant: 'destructive',
    });
    return false;
  };

  const handleGoogle = async () => {
    if (!token || !inviteData?.email) return;
    setIsGoogleLoading(true);

    try {
      // Lets handle_new_user accept the Google account for the invited email.
      const { data: booked, error: bookError } = await supabase.rpc('book_oauth_invite_ticket', {
        p_token: token,
        p_kind: 'company',
      });
      if (bookError) throw bookError;
      if (!booked) throw new Error('This invitation has expired or is no longer valid.');

      markOAuthInvite('company', token);
      const { error } = await signInWithGoogle({
        returnPath: '/company-invite/accept',
        loginHint: inviteData.email,
      });
      if (error) throw error;
      // On success the browser is already leaving for Google.
    } catch (err) {
      clearOAuthInvite();
      clearPendingOAuthLogin();
      setIsGoogleLoading(false);
      toast({
        title: 'Could not continue with Google',
        description: (err as { message?: string })?.message || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const validateInvite = async () => {
    if (!token) {
      console.error('❌ No company invite token provided');
      setInviteValid(false);
      setIsValidating(false);
      toast({
        title: "No Token",
        description: "No company invitation token found in URL",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('🔍 Validating company invite token...');
      
      const { data, error } = await supabase.rpc('validate_company_invite_token', { 
        p_token: token 
      });

      if (error) {
        console.error('❌ RPC call failed:', error);
        toast({
          title: "Validation Error",
          description: error.message,
          variant: "destructive",
        });
        setInviteValid(false);
        setIsValidating(false);
        return;
      }

      const result = (Array.isArray(data) ? data[0] : data) as CompanyInviteValidation;
      
      console.log('🔍 Validation result:', result);
      
      if (!result || result.is_valid !== true) {
        const errorMsg = result?.error_message || 'This company invitation is invalid or has expired';
        console.warn('⚠️ Company invite is INVALID:', errorMsg);
        toast({
          title: "Invalid Invitation",
          description: errorMsg,
          variant: "destructive",
        });
        setInviteValid(false);
      } else {
        console.log('✅ Company invite is VALID!', {
          email: result.email,
          company_name: result.company_name,
        });
        setInviteValid(true);
        setInviteData(result);
        toast({
          title: "Valid Invitation",
          description: `You've been invited to create ${result.company_name}`,
        });
      }
    } catch (err) {
      console.error('❌ Unexpected error during validation:', err);
      toast({
        title: "Error",
        description: "An unexpected error occurred while validating the invitation",
        variant: "destructive",
      });
      setInviteValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const validated = signupSchema.parse({ fullName, password, confirmPassword });
      
      console.log('🔐 Creating founder account for:', inviteData?.email);
      console.log('🏢 Company:', inviteData?.company_name);
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: inviteData?.email || '',
        password: validated.password,
        options: {
          emailRedirectTo: `${APP_CONFIG.appUrl}/company-setup`,
          data: {
            full_name: validated.fullName,
            company_invite_token: token, // REQUIRED by handle_new_user() trigger
          },
        },
      });

      if (authError) {
        console.error('❌ Signup failed:', authError);
        
        // Check if user already exists
        if (authError.message.includes('already registered') || authError.message.includes('already been registered')) {
          toast({
            title: "Account Already Exists",
            description: "An account with this email already exists. Please contact support.",
            variant: "destructive",
          });
          return;
        }
        
        // Check if the DB trigger rejected due to invalid invite
        if (authError.message.includes('Account creation requires a valid invitation') ||
            authError.message.includes('Invalid or expired') ||
            authError.message.includes('email does not match')) {
          toast({
            title: "Invitation Invalid",
            description: "This invitation has expired or is no longer valid. Please request a new invitation.",
            variant: "destructive",
          });
          return;
        }
        
        throw authError;
      }

      if (!authData.user) {
        throw new Error('No user data returned from signup');
      }

      console.log('✅ Founder account created successfully! User ID:', authData.user.id);
      
      setAccepted(true);
      
      toast({
        title: 'Success!',
        description: 'Your account has been created. Redirecting to login...',
      });
      
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    } catch (error) {
      console.error('❌ Error during account creation:', error);
      
      if (error instanceof z.ZodError) {
        toast({
          title: 'Validation error',
          description: error.errors[0].message,
          variant: 'destructive',
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create account';
        toast({
          title: 'Error creating account',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-lg">Validating company invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!inviteValid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-16 w-16 text-destructive" />
            </div>
            <CardTitle className="text-center">Invalid Invitation</CardTitle>
            <CardDescription className="text-center">
              This company invitation link is invalid, expired, or has already been used.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/auth')}>
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <CheckCircle2 className="h-16 w-16 text-success" />
            </div>
            <CardTitle className="text-center">Account Created!</CardTitle>
            <CardDescription className="text-center">
              Your account and company <strong>{inviteData?.company_name}</strong> have been created. Redirecting to sign in...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-center mb-2">
            <Building2 className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="text-center">Create Your Company</CardTitle>
          <CardDescription className="text-center">
            You've been invited to set up <strong>{inviteData?.company_name}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {googleEnabled && (
            <>
              <GoogleButton onClick={handleGoogle} isLoading={isGoogleLoading} disabled={isLoading} />
              <AuthDivider className="my-6" label="or create a password" />
            </>
          )}
          <form onSubmit={handleCreateAccount} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={inviteData?.email || ''} disabled />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName">Your Full Name</Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="John Doe"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account & Company'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
